import chevronLeftIcon from "@loot-os/icons/icons/chevron-left";
import chevronRightIcon from "@loot-os/icons/icons/chevron-right";
import { Children, type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { cn } from "../foundations/cn.js";
import { CAROUSEL_SLIDES } from "./Carousel.styles.js";
import { IconButton } from "./IconButton.js";

export interface CarouselProps {
  /** What the row holds: "Featured boxes". Names the region. */
  label: string;
  /** Shown before the buttons: a SectionHeader's title, or nothing. */
  heading?: ReactNode;
  /** One child per slide. */
  children: ReactNode;
  /**
   * Each slide's width as complete classes (Tailwind reads literals only): one of
   * `CAROUSEL_SLIDES` (`Carousel.styles.ts`), `wide` by default, so the next slide always peeks.
   */
  slideClassName?: string;
  /** The buttons' names. */
  previousLabel?: string;
  nextLabel?: string;
  /** A slide's position, read with it: "3 of 8". */
  slideLabel?: (position: number, count: number) => string;
  className?: string;
}

/**
 * A sideways row of slides that scrolls by a page with Previous and Next, and by swipe or
 * trackpad. It never moves on its own. Every slide stays in the page's tab order, so a keyboard
 * reaches each without the buttons.
 */
export function Carousel({
  label,
  heading,
  children,
  slideClassName = CAROUSEL_SLIDES.wide,
  previousLabel = "Previous",
  nextLabel = "Next",
  slideLabel = (position, count) => `${position} of ${count}`,
  className,
}: CarouselProps) {
  const slides = Children.toArray(children);
  const track = useRef<HTMLUListElement>(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  const measure = useCallback(() => {
    const el = track.current;
    if (!el) return;
    // A pixel of slack: fractional widths leave the last scroll a hair short of the end.
    setEdges({
      start: el.scrollLeft <= 1,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    measure();
    const el = track.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  const page = (direction: 1 | -1) => {
    const el = track.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: reduce ? "auto" : "smooth" });
  };

  const scrolls = !(edges.start && edges.end);
  return (
    <section
      aria-roledescription="carousel"
      aria-label={label}
      className={cn("flex flex-col gap-4", className)}
    >
      {(heading || scrolls) && (
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0 flex-1">{heading}</div>
          {scrolls && (
            <div className="flex shrink-0 gap-2">
              <IconButton
                icon={chevronLeftIcon}
                label={previousLabel}
                variant="secondary"
                size="sm"
                disabled={edges.start}
                onClick={() => page(-1)}
              />
              <IconButton
                icon={chevronRightIcon}
                label={nextLabel}
                variant="secondary"
                size="sm"
                disabled={edges.end}
                onClick={() => page(1)}
              />
            </div>
          )}
        </div>
      )}
      <ul
        ref={track}
        onScroll={measure}
        className="-mx-1 flex snap-x snap-mandatory scroll-px-1 gap-4 overflow-x-auto overscroll-x-contain px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {slides.map((slide, index) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: Children.toArray keys the slides; the index is their place.
            key={index}
            aria-roledescription="slide"
            aria-label={slideLabel(index + 1, slides.length)}
            // min-w-0: a slide is its basis wide, never wider for long text inside it, so every
            // slide in a row is one size and its own text truncates.
            className={cn("min-w-0 shrink-0 snap-start", slideClassName)}
          >
            {slide}
          </li>
        ))}
      </ul>
    </section>
  );
}
